import React, { FunctionComponent, ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ButtonsContainer,
  ButtonsContainerAlignment
} from 'record-it-components/button/ButtonsContainer/ButtonsContainer';
import {
  ButtonStandard,
  ButtonStandardType
} from 'record-it-components/button/ButtonStandard/ButtonStandard';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import {
  LocationInput,
  LocationInputProps
} from 'record-it-components/input/LocationInput/LocationInput';
import {
  BufferedTextInput,
  TextInputProps
} from 'record-it-components/input/TextInput/TextInput';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { ConstructionSite } from '../../../classes/entities/ConstructionSite/constructionSiteTypes';
import {
  TimeTrackingActivityCompilationSelect,
  TimeTrackingActivityCompilationSelectProps
} from '../../timeTrackingActivityCompilation/TimeTrackingActivityCompilationSelect/TimeTrackingActivityCompilationSelect';

export type CreateConstructionSiteFormProps = PropsWithoutChildren<
  ComponentProps<'div'>
> & {
  userGroupId: string;
  defaultTimeTrackingActivityCompilationId: string;
  onCreateConstructionSite: (
    constructionSiteToCreate: ConstructionSiteToCreate
  ) => void;
  onCancel: () => void;
};

export const CreateConstructionSiteForm: FunctionComponent<CreateConstructionSiteFormProps> =
  React.memo((props) => {
    const {
      userGroupId,
      defaultTimeTrackingActivityCompilationId,
      onCreateConstructionSite,
      onCancel,
      ...remainingProps
    } = prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation();

    const [constructionSiteToCreate, setConstructionSiteToCreate] =
      useState<ConstructionSiteToCreate>(() => {
        return {
          name: '',
          location: null,
          compilationId: defaultTimeTrackingActivityCompilationId
        };
      });

    const handleNameChanged: TextInputProps['onInput'] = useEventCallback(
      (name) => {
        setConstructionSiteToCreate({
          ...constructionSiteToCreate,
          name
        });
      }
    );

    const handleCompilationIdChanged: TimeTrackingActivityCompilationSelectProps['onValueChanged'] =
      useEventCallback((compilationId) => {
        setConstructionSiteToCreate({
          ...constructionSiteToCreate,
          compilationId
        });
      });

    const handleLocationChanged: LocationInputProps['onValueChanged'] =
      useEventCallback((location) => {
        setConstructionSiteToCreate({
          ...constructionSiteToCreate,
          location
        });
      });

    const handleCreateClick = useEventCallback(() => {
      onCreateConstructionSite(constructionSiteToCreate);
    });

    return (
      <div {...remainingProps}>
        <FormGroup>
          <BufferedTextInput
            label={t('entities.ConstructionSite.name')}
            value={constructionSiteToCreate.name}
            onInput={handleNameChanged}
            data-testid="CreateConstructionSiteForm.NameInput"
          ></BufferedTextInput>
        </FormGroup>

        <FormGroup>
          <TimeTrackingActivityCompilationSelect
            value={constructionSiteToCreate.compilationId}
            label={t('entities.ConstructionSite.compilationId')}
            userGroupId={userGroupId}
            onValueChanged={handleCompilationIdChanged}
          ></TimeTrackingActivityCompilationSelect>
        </FormGroup>

        <FormGroup>
          <LocationInput
            value={constructionSiteToCreate.location}
            label={t('entities.ConstructionSite.location')}
            onValueChanged={handleLocationChanged}
          ></LocationInput>
        </FormGroup>

        <ButtonsContainer alignment={ButtonsContainerAlignment.Right}>
          <ButtonStandard
            type={ButtonStandardType.Secondary}
            onClick={onCancel}
            data-testid="CreateConstructionSiteForm.CancelButton"
          >
            {t('components.constructionSite.CreateConstructionSiteForm.cancel')}
          </ButtonStandard>
          <ButtonStandard
            type={ButtonStandardType.Primary}
            onClick={handleCreateClick}
            data-testid="CreateConstructionSiteForm.CreateButton"
          >
            {t('components.constructionSite.CreateConstructionSiteForm.create')}
          </ButtonStandard>
        </ButtonsContainer>
      </div>
    );
  });

export type ConstructionSiteToCreate = Pick<
  ConstructionSite,
  'name' | 'location' | 'compilationId'
>;
